// src/app/components/DeviceTypePicker.tsx
import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Group,
  Menu,
  Stack,
  Text,
  ScrollArea,
  Badge,
  TextInput,
  Tooltip,
  Loader,
  Paper,
  Button,
} from "@mantine/core";
import { FaPlus } from "react-icons/fa";
import { useProjectId } from "../../hooks/useProjectId";
import { IDeviceType } from "@backend/src/shared/schemas/schemas";
import { openapi } from "../../../openapi";
import { DeviceCategoryEnum, DEVICES_TYPES_SETTINGS } from "@multiconsult-group/multiplacer-core";
import { IDeviceCategoryEnum } from "@backend/src/shared/schemas/foundationSchema";

type Props = {
  onSelect: (deviceType: IDeviceType) => void;
  onSelectGeneric: (deviceCategory: IDeviceCategoryEnum) => void;
  disabled: boolean;
};

const CEILING_DEVICE_TYPE_CATEGORIES = [
  DeviceCategoryEnum.SUPPLY_AIR_DEVICE,
  DeviceCategoryEnum.RETURN_AIR_DEVICE,
  DeviceCategoryEnum.LIGHT_DEVICE,
  DeviceCategoryEnum.SENSOR_DEVICE,
  DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE,
  DeviceCategoryEnum.CHILLER,
] satisfies IDeviceCategoryEnum[];

export const DeviceTypePicker: React.FC<Props> = ({ onSelect, onSelectGeneric, disabled }) => {
  const projectNo = useProjectId();
  const [opened, setOpened] = useState(false);
  const [activeCategory, setActiveCategory] = useState<IDeviceCategoryEnum | null>(null);
  const [search, setSearch] = useState("");

  const {
    data: deviceTypes,
    isLoading,
    isError,
  } = openapi.endpoints.getMultiplacerDevicetypes.useQuery({ projectNo });

  const categories = useMemo<IDeviceCategoryEnum[]>(() => [...CEILING_DEVICE_TYPE_CATEGORIES], []);

  // Map category -> deviceTypes
  const typesByCategory = useMemo(() => {
    const map = new Map<IDeviceCategoryEnum, IDeviceType[]>();

    for (const cat of categories) {
      map.set(cat, []);
    }

    if (!deviceTypes) return map;

    for (const dt of deviceTypes as IDeviceType[]) {
      const cat = dt.deviceCategory as IDeviceCategoryEnum | undefined;
      if (!cat) continue;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(dt);
    }

    // sortera alfabetiskt per kategori
    for (const [, arr] of map.entries()) {
      arr.sort((a, b) => {
        const ac = (a.compCode ?? "").localeCompare(b.compCode ?? "");
        if (ac !== 0) return ac;
        return (a.typeName ?? "").localeCompare(b.typeName ?? "");
      });
    }

    return map;
  }, [deviceTypes, categories]);

  const visibleCategories = useMemo(
    () =>
      categories
        .sort((a, b) => {
          const labelA = DEVICES_TYPES_SETTINGS[a]?.label ?? a;
          const labelB = DEVICES_TYPES_SETTINGS[b]?.label ?? b;
          return labelA.localeCompare(labelB);
        }),
    [categories]
  );

  // Default aktiv kategori: første kategori. Generiske komponenter skal alltid kunne legges til.
  const effectiveActiveCategory: IDeviceCategoryEnum | null = useMemo(() => {
    if (activeCategory && visibleCategories.includes(activeCategory)) {
      return activeCategory;
    }
    if (!visibleCategories.length) return null;
    return visibleCategories[0];
  }, [activeCategory, visibleCategories]);

  const filteredTypes = useMemo(() => {
    if (!effectiveActiveCategory) return [];
    const list = typesByCategory.get(effectiveActiveCategory) ?? [];
    if (!search.trim()) return list;

    const s = search.toLowerCase();
    return list.filter((dt) => {
      const code = (dt.compCode ?? "").toLowerCase();
      const name = (dt.typeName ?? "").toLowerCase();
      return code.includes(s) || name.includes(s);
    });
  }, [effectiveActiveCategory, typesByCategory, search]);

  const handleSelect = (dt: IDeviceType) => {
    onSelect(dt);
    setOpened(false);
  };

  const handleSelectGeneric = (category: IDeviceCategoryEnum) => {
    onSelectGeneric(category);
    setOpened(false);
  };

  const renderDropdownContent = () => {
    return (
      <Box px="xs" py="xs" w={520}>
        <Group align="flex-start" gap="xs">
          {/* Kategorier (venstre) */}
          <Stack gap={4} w={180}>
            {visibleCategories.map((cat) => {
              const settings = DEVICES_TYPES_SETTINGS[cat];
              const color = settings?.color ?? "#cccccc";
              const label = settings?.label ?? cat;
              const description = settings?.description;
              const count = (typesByCategory.get(cat) ?? []).length;
              const isActive = effectiveActiveCategory === cat;

              return (
                <Box
                  key={cat}
                  px={8}
                  py={6}
                  style={{
                    borderRadius: 6,
                    cursor: "pointer",
                    backgroundColor: isActive ? "rgba(0,0,0,0.04)" : "transparent",
                  }}
                  onClick={() => setActiveCategory(cat)}
                >
                  <Group gap={8}>
                    <Box
                      w={14}
                      h={14}
                      style={{
                        borderRadius: 4,
                        border: "1px solid rgba(0,0,0,0.2)",
                        backgroundColor: color,
                      }}
                    />
                    <Stack gap={0} style={{ flex: 1 }}>
                      <Text size="xs" fw={500}>
                        {label}
                      </Text>
                    </Stack>
                  </Group>
                </Box>
              );
            })}
          </Stack>

          {/* Divider-liknande spacing */}
          <Box w={1} h="100%" bg="gray.2" />

          {/* Typer (høyre) */}
          <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
            {isLoading && (
              <Group gap={8}>
                <Loader size="xs" />
                <Text size="xs" c="dimmed">
                  Laster prosjektets device-typer…
                </Text>
              </Group>
            )}
            {isError && (
              <Text size="xs" c="red">
                Kunne ikke hente device-typer. Generiske komponenter kan fortsatt legges til.
              </Text>
            )}
            <TextInput
              size="xs"
              placeholder="Søk på compcode / navn…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
            />
            <ScrollArea.Autosize mah={320} type="auto">
              <Stack gap={4} pt={4}>
                {effectiveActiveCategory && (
                  <Box
                    px={8}
                    py={8}
                    style={{
                      borderRadius: 6,
                      cursor: "pointer",
                      border: "1px dashed rgba(25,113,194,0.45)",
                      backgroundColor: "rgba(25,113,194,0.04)",
                    }}
                    onClick={() => handleSelectGeneric(effectiveActiveCategory)}
                  >
                    <Group gap={8} wrap="nowrap">
                      <FaPlus size={12} color="#1971c2" />
                      <Stack gap={0}>
                        <Text size="sm" fw={600}>
                          Generisk {DEVICES_TYPES_SETTINGS[
                            effectiveActiveCategory
                          ]?.label?.toLowerCase()}
                        </Text>
                        <Text size="xs" c="dimmed">
                          Velg størrelse i himlingsplater
                        </Text>
                      </Stack>
                    </Group>
                  </Box>
                )}
                {effectiveActiveCategory && filteredTypes.length === 0 && (
                  <Text size="xs" c="dimmed">
                    Ingen typer i denne kategorien som matcher søket.
                  </Text>
                )}
                {filteredTypes.map((dt) => (
                  <Box
                    key={dt._id as any}
                    px={8}
                    py={6}
                    style={{
                      borderRadius: 6,
                      cursor: "pointer",
                      border: "1px solid transparent",
                    }}
                    onClick={() => handleSelect(dt)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.border = "1px solid rgba(0,0,0,0.15)";
                      e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.02)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.border = "1px solid transparent";
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <Stack gap={0}>
                      <Text size="sm" fw={500}>
                        {dt.compCode || "Ingen compcode"}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={2}>
                        {dt.typeName}
                      </Text>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          </Stack>
        </Group>
      </Box>
    );
  };

  return (
    <Menu width="auto" position="bottom-start" opened={opened} onChange={setOpened} withinPortal>
      <Tooltip label="Legg til komponent i viewer">
        <Menu.Target>
          <ActionIcon
            disabled={disabled}
            variant="outline"
            color="blue"
            radius="sm"
            aria-label="Legg til komponent"
          >
            <FaPlus size={14} />
          </ActionIcon>
        </Menu.Target>
      </Tooltip>

      <Menu.Dropdown p={6}>
        <Paper
          radius="lg"
          shadow="md"
          withBorder
          // Svagt grå bakgrunn som skiller seg fra panelen bak
          bg="var(--mantine-color-gray-0)"
        >
          {renderDropdownContent()}
        </Paper>
      </Menu.Dropdown>
    </Menu>
  );
};

export default DeviceTypePicker;
